import {
  useMutation,
  useQueryClient,
  useQuery,
  MutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseMutationOptions,
} from 'react-query'
import {
  AddMembershipChargePayment,
  CreateMembershipSmsRequest,
  Membership,
  MembershipChargeResponse,
  MembershipsApi as MembershipsApiTs,
  ResponseError,
  ShowMembership,
  ShowMembershipSms,
} from '@trybeapp/sdk'
import { MembershipsApi } from '@trybeapp/sdk.customer'
import { BasketApi } from '@trybeapp/sdk.shop'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { DateTime } from 'luxon'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { toIsoDate } from 'utilities/DateUtils/dateUtils'
import { ApiResponse, LegacySdkResponseError, PaginatedApiResponse } from 'api/api.types'

const api = new MembershipsApi()
const basketApi = new BasketApi()
const membershipsApiTs = new MembershipsApiTs(apiConfiguration)

export const useMembershipsForCustomer = (customerId: string, options: UseQueryOptions = {}) =>
  useQuery<unknown, unknown, any>(
    ['memberships', customerId],
    async () =>
      await api.listMemberships({
        customerId: customerId,
      }),
    options
  )

export const useMemberships = (
  params: Record<string, any> = {},
  options: UseQueryOptions<PaginatedApiResponse<any>, LegacySdkResponseError> = {}
) => {
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes

  params.siteId = useCurrentSiteId()
  params.createdAtFrom = params.createdAtFrom ? toIsoDate(params.createdAtFrom) : null
  params.createdAtTo = params.createdAtTo ? toIsoDate(params.createdAtTo) : null
  params.endDateFrom = params.endDateFrom ? toIsoDate(params.endDateFrom) : null
  params.endDateTo = params.endDateTo ? toIsoDate(params.endDateTo) : null
  params.nextBillingDateFrom = params.nextBillingDateFrom
    ? DateTime.fromJSDate(params.nextBillingDateFrom).toISODate()
    : null
  params.nextBillingDateTo = params.nextBillingDateTo
    ? DateTime.fromJSDate(params.nextBillingDateTo).toISODate()
    : null
  return useQuery<PaginatedApiResponse<any>, LegacySdkResponseError>(
    ['memberships', { ...params }],
    () => api.listMemberships(params),
    options
  )
}

export const useExportMemberships = () => {
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, Record<string, any>>(
    ['membershipExport'],
    (params = {}) => {
      params.siteId = siteId
      params.createdAtFrom = params.createdAtFrom ? toIsoDate(params.createdAtFrom) : null
      params.createdAtTo = params.createdAtTo ? toIsoDate(params.createdAtTo) : null
      params.endDateFrom = params.endDateFrom ? toIsoDate(params.endDateFrom) : null
      params.endDateTo = params.endDateTo ? toIsoDate(params.endDateTo) : null
      params.nextBillingDateFrom = params.nextBillingDateFrom
        ? toIsoDate(params.nextBillingDateFrom)
        : null
      params.nextBillingDateTo = params.nextBillingDateTo
        ? toIsoDate(params.nextBillingDateTo)
        : null

      return api.queueListMemberships(params)
    }
  )
}

export const useCustomerMembership = (
  membershipId: string,
  options: UseQueryOptions<ApiResponse<Membership>, LegacySdkResponseError> = {}
) =>
  useQuery<ApiResponse<Membership>, LegacySdkResponseError>(
    ['membership', membershipId],
    () => api.getMembership(membershipId),
    options
  )

export const useMembership = (
  membershipId: string,
  options: UseQueryOptions<ShowMembership, ResponseError> = {}
) =>
  useQuery<ShowMembership, ResponseError>(
    ['membershipTs', membershipId],
    () => membershipsApiTs.getMembership({ membershipId }),
    options
  )

export const useConfirmMembership = () => {
  const queryClient = useQueryClient()

  return useMutation((membershipId) => api.confirmMembership(membershipId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['memberships'])
    },
  })
}

export const useCreateCustomerMembership = (
  customerId: string,
  options: UseMutationOptions<ApiResponse<any>, LegacySdkResponseError, Record<string, any>> = {}
) => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, Record<string, any>>(
    async (membership) =>
      basketApi.createMembershipOrder({
        customer_id: customerId,
        site_id: siteId,
        ...membership,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['memberships'])
        if (options.onSuccess) {
          options.onSuccess(...args)
        }
      },
    }
  )
}

export const useCancelMembership = (membershipId?: string) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, string | null>(
    (passedMembershipId = null) => api.cancelMembership(membershipId ?? passedMembershipId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useRequestMandate = (membershipId: string) =>
  useMutation(() => api.requestMandate(membershipId))

export const useRequestMandateForMembership = () =>
  useMutation((membershipId: string) => api.requestMandate(membershipId))

export const useRetryPaymentForMembership = () =>
  useMutation((membershipId: string) => api.retryPayment(membershipId))

export const useUpdateMembership = (membershipId: string, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation<any, any, any>(
    (updateMembershipRequest) => api.updateMembership(membershipId, { updateMembershipRequest }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useAddMembershipPayment = (options: MutationOptions = {}) => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ membershipId, paymentMethodId }: any) =>
      api.addManualPayment(membershipId, {
        addManualPaymentRequest: {
          processor_type_id: paymentMethodId,
        },
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Invalidate the list of memberships because we've updated the next billing date of this one.
        queryClient.invalidateQueries(['memberships'])

        if (options.onSuccess) {
          options.onSuccess(data, variables, context)
        }
      },
    }
  )
}

export const useAddMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string, AddMembershipChargePayment]>(
    ([chargeId, addMembershipChargePayment]) => {
      return membershipsApiTs.actionAddMembershipChargePayment({
        chargeId,
        addMembershipChargePayment,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useProcessMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string]>(
    ([chargeId]) => {
      return membershipsApiTs.actionProcessMembershipCharge({
        chargeId,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useRetryMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string]>(
    ([chargeId]) => {
      return membershipsApiTs.actionRetryMembershipCharge({
        chargeId,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useArchiveMembership = () => {
  const queryClient = useQueryClient()

  return useMutation((membershipId) => api.archiveMembership(membershipId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['memberships'])
    },
  })
}

export const useNextBillingDetails = (
  membershipId: string,
  options: UseQueryOptions = {}
): UseQueryResult<any> =>
  useQuery<unknown, unknown, any>(
    ['membershipNextBillingDetails', membershipId],
    () => api.nextBillingDetails(membershipId),
    options
  )

export const useSubsequentBillingDetails = (
  membershipId: string,
  options: UseQueryOptions = {}
): UseQueryResult<any> =>
  useQuery<unknown, unknown, any>(
    ['membershipSubsequentBillingDetails', membershipId],
    () => api.subsequentBillingDetails(membershipId),
    options
  )

export const useSendMembershipSms = () =>
  useMutation<ShowMembershipSms, ResponseError, CreateMembershipSmsRequest>((params) =>
    membershipsApiTs.createMembershipSms(params)
  )
